import { z } from 'zod';
/**
 * Workflows-owned DUPLICATED consumer contract for the Tasks-owned
 * `tasks.workflow-actions.v1` approved-action provider. Workflows deliberately
 * imports no Tasks package: these shapes are copies of the Tasks contract
 * (consumer-contract duplication), and an executable apps/api compatibility
 * test keeps them equal to the Tasks contract. Execution flows only through
 * the neutral Platform optional-action-provider host; the owner mutation
 * happens in the Tasks provider under the publisher's revalidated authority.
 */
export const workflowsTaskActionOwner = {
    moduleId: 'tasks',
    contributionId: 'tasks.workflow-actions.v1',
    slot: 'workflows.approved-owner-actions',
};
export const workflowsTaskActionContractVersion = 'tasks.workflow-actions.v1';
/**
 * The CANONICAL Task-create action id: the only id new rule authoring emits and
 * the only id the Tasks owner action is registered under.
 */
export const workflowsTaskCreateActionId = 'tasks.create-task.v1';
/**
 * Legacy authored ids retained in IMMUTABLE historical rule versions. They are
 * never rewritten — the authored id stays in the attempt and execution
 * evidence — and they resolve to the single canonical owner action above.
 * There is exactly one Task-create implementation.
 */
export const workflowsTaskCreateLegacyActionIds = ['tasks.task-create.v1'];
/** Every authored id this worker's single action family accepts. */
export const workflowsTaskCreateAcceptedActionIds = [
    workflowsTaskCreateActionId,
    ...workflowsTaskCreateLegacyActionIds,
];
export function isWorkflowsTaskCreateActionId(actionId) {
    return workflowsTaskCreateAcceptedActionIds.includes(actionId);
}
/**
 * sha256 of the canonical JSON encoding of the create-task input schema's
 * JSON-Schema artifact, duplicated from the Tasks contract; a drifted owner
 * descriptor hash makes the execution fail `action-contract-changed`.
 */
export const workflowsTaskCreateInputSchemaHash = '8ef1b089d791d1b4c478f4f7d53fb445daf077042e5ad3a49a5b4a19a07a0b7e';
export const workflowsActionIntentExecutionJobId = 'workflows.jobs.action-intent-execution';
const uuid = z.string().uuid();
const schemaHash = z.string().regex(/^[a-f0-9]{64}$/);
/**
 * Rule-authored configuration for the workflow create-task action (duplicate
 * of the Tasks configuration schema, bounds and defaults included). Execution
 * supplies only the target Project identity on top of it.
 */
export const workflowsTaskCreateConfigurationSchema = z.strictObject({
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().min(1).max(4000).nullable().default(null),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
    dueInDays: z.number().int().min(0).max(365).nullable().default(null),
});
/**
 * Full action input at execution time: the rule-authored configuration plus
 * the resolved target Project (duplicate of the Tasks input schema).
 */
export const workflowsTaskCreateInputSchema = z.strictObject({
    ...workflowsTaskCreateConfigurationSchema.shape,
    projectId: uuid,
});
/**
 * Duplicate of the Tasks-owned execution request for one pending workflow
 * intent. The commandId is the stable, per-intent idempotent Tasks create
 * command, and intentCreatedAt is the intent's immutable creation instant:
 * the owner derives every time-dependent value (the due date) from it, never
 * from execution time, so a replayed execution submits the byte-identical
 * command payload and returns the original task instead of creating another.
 * The executable apps/api compatibility test keeps this shape equal to the
 * Tasks contract.
 */
export const workflowsTaskExecuteRequestSchema = z.strictObject({
    kind: z.literal('execute-workflow-intent'),
    contractVersion: z.literal(workflowsTaskActionContractVersion),
    executionId: uuid,
    intentId: uuid,
    runId: uuid,
    ruleId: uuid,
    ruleVersionId: uuid,
    sourceEventId: uuid,
    commandId: uuid,
    intentCreatedAt: z.string().datetime({ offset: true }),
    actionId: z.literal(workflowsTaskCreateActionId),
    authoredActionId: z.enum(workflowsTaskCreateAcceptedActionIds),
    inputSchemaHash: schemaHash,
    input: workflowsTaskCreateInputSchema,
});
/**
 * Duplicate of the Tasks-owned execution result union (owner readback on
 * success, typed owner failure otherwise). The executable apps/api
 * compatibility test keeps this shape equal to the Tasks contract.
 */
export const workflowsTaskExecuteResultSchema = z.discriminatedUnion('outcome', [
    z.strictObject({
        outcome: z.literal('completed'),
        contractVersion: z.literal(workflowsTaskActionContractVersion),
        readback: z.strictObject({
            subjectType: z.literal('tasks.task'),
            taskId: uuid,
            projectId: uuid,
            status: z.string().min(1).max(40),
            title: z.string().min(1).max(240),
            replayed: z.boolean(),
        }),
    }),
    z.strictObject({
        outcome: z.literal('failed'),
        contractVersion: z.literal(workflowsTaskActionContractVersion),
        failure: z.strictObject({
            code: z.enum([
                'input-invalid',
                'schema-mismatch',
                'permission-denied',
                'target-unavailable',
                'action-failed',
            ]),
            message: z.string().trim().min(1).max(500),
            retryable: z.boolean(),
        }),
    }),
]);
/**
 * Terminal failure codes the execution JOB itself stamps from revalidation
 * evidence. Owner failure codes from the execute result union are stored
 * as-is alongside these. There is deliberately NO generic retry-budget
 * terminal: transient conditions (owner absence, uncertain mutations,
 * infrastructure failures) stay nonterminal under the expired-lease retry so
 * a recoverable intent is never destroyed; claim_count remains
 * fencing/diagnostic evidence only.
 */
export const workflowsActionExecutionFailureCodes = [
    'rule-no-longer-active',
    'authority-membership-inactive',
    'authority-account-inactive',
    'execution-authority-unknown',
    'permission-revoked',
    'source-event-unavailable',
    'action-unsupported',
    'configuration-invalid',
    'action-contract-changed',
];
//# sourceMappingURL=workflows-task-action-consumer.contract.js.map